"use client"

import { Spinner, Text } from "@/elements"
import { cn } from "@/utils/classnames"
import React, { useEffect, forwardRef } from "react"
import { ButtonSize, ButtonVariant } from "./types"
import clsx from "clsx"
import { ProLockIcon } from "@/icons/outline/ProLockIcon"

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: ButtonSize
  variant?: ButtonVariant
  loading?: boolean
  active?: boolean
  floating?: boolean
  success?: boolean
  LeftElement?: React.FC | null
  RightElement?: React.FC | null
  disabled?: boolean
  fontWeight?: "default" | "thin"
}

const sizeSmallClasses = "h-[32px] px-3"
const sizeMediumClasses = "h-[40px] px-5"
const sizeLargeClasses = "h-[56px] px-8"
const buttonSpinnerColor: Record<ButtonVariant, string> = {
  primary: "bg-bg-1",
  secondary: "bg-primary-3",
  tertiary: "bg-fg-1",
  transparent: "bg-fg-1",
  white: "bg-fg-1",
  "grey-fill": "bg-fg-1",
  "grey-outline": "bg-fg-1",
  error: "bg-bg-1",
  "grey-dark": "bg-fg-1",
  "error-outline": "bg-bg-1",
  "pro-primary": "bg-pro-gradient",
  "pro-gradient": "bg-fg-1",
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      loading = false,
      active = false,
      success,
      size = "medium",
      variant = "primary",
      LeftElement,
      RightElement,
      floating = false,
      className,
      disabled = false,
      fontWeight = "default",
      ...rest
    },
    ref
  ) => {
    useEffect(() => {
      if (loading && ref && "current" in ref && ref.current !== null) {
        ;(ref as React.RefObject<HTMLButtonElement | null>).current?.blur()
      }
    }, [loading, ref])

    const isThin = fontWeight === "thin"

    return (
      <>
        <button
          ref={ref}
          disabled={loading || disabled}
          className={cn(
            "relative inline-flex items-center justify-center",
            size === "medium" && sizeMediumClasses,
            size === "small" && sizeSmallClasses,
            size === "large" && sizeLargeClasses,
            loading && "cursor-not-allowed",
            className,
            `button-${variant}`,
            `hover-enabled:button-${variant}`,
            disabled && "cursor-not-allowed",
            active && `button-${variant}-active`,
            variant === "pro-gradient" && "bg-pro-gradient"
          )}
          style={
            floating
              ? {
                  boxShadow: "0px 8px 25px 0px rgba(0, 0, 0, 0.20)",
                }
              : {}
          }
          {...rest}>
          <div
            className={clsx(
              "flex w-full flex-nowrap items-center justify-center gap-x-2 transition-opacity duration-200",
              loading ? "opacity-0" : "opacity-100"
            )}>
            {LeftElement && <LeftElement />}
            <Text
              className={clsx("flex flex-shrink-0", variant === "pro-primary" ? "text-pro-gradient" : "")}
              variant={isThin ? "label" : "CTASmall"}>
              {children}
            </Text>
            {RightElement ? <RightElement /> : null}
          </div>
          <div
            className={`absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center transition-opacity duration-200 ${loading ? "opacity-100" : "opacity-0"}`}>
            <Spinner className={buttonSpinnerColor[variant]} size="small" />
          </div>
        </button>
      </>
    )
  }
)

Button.displayName = "Button"
